import {useEffect} from "react";
import {useFormContext} from "react-hook-form";

import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";

export type FormValuesKey = keyof IntegrationFormData;

interface StatusIOFormProps {
  serverErrors?: ServerErrors;
}

export const StatusForm = ({serverErrors}: StatusIOFormProps) => {
  const {setError} = useFormContext();

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FormValuesKey, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  return (
    <>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.statuspage_id"
            label="Status Page ID"
            required={true}
            tooltip="Listed on the API page."
            maxLength={24}
            autoFocus={true}
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.api_id"
            label="API ID"
            required={true}
            tooltip="Listed on the API page."
            maxLength={36}
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.api_key"
            label="API Key"
            required={true}
            tooltip="Listed on the API page."
            maxLength={256}
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.component"
            label="Component ID"
            required={false}
            tooltip="Component to update with availability status. Listed in the Infrastructure / Modify Component / API section."
            maxLength={24}
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.container"
            label="Container ID"
            required={false}
            tooltip="Container to update with availability status. Listed in the Infrastructure / Modify Component / API section."
            maxLength={24}
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.metric"
            label="Metric ID"
            required={false}
            tooltip="Metric to update with response time data. Listed on the Metrics / Modify Metric page when setting Data Source to Custom."
            maxLength={24}
          />
        </div>
      </div>
      <SetupInfo metricsWarning={true}>
        <div className="p-2">
          From the Status.io dashboard, click the "API" tab from the left menu, and under "Developer API" click the
          "Display API Credentials" button. Now copy the "Status Page ID", "API ID" and "API Key" into the corresponding
          fields in the form above.
        </div>
        <div className="p-2">
          To have a component &amp; container automatically update when a check fails, you need to provide the
          "Component ID" and "Container ID". You can find these by clicking the "Infrastructure" tab from the left menu,
          selecting the "Modify" link next to your component, scrolling down to "Automatic Status Updates" and clicking
          "API". Copy both the component and container ID's into the form above.
          <br />
          <br />
          To have a metric update with a check's response time, select "Metrics" from the left menu and add or modify a
          metric. Under "Data Source" select "Custom". Then copy the "Metric ID" into the form above.
        </div>
        <div className="p-2">
          <a target="_blank" href="https://support.uptime.com/hc/en-us/articles/115002561385-Configuring-Status-io">
            View {IntegrationModuleInfo[IntegrationModules.STATUS].label} Support Article &rarr;
          </a>
        </div>
      </SetupInfo>
    </>
  );
};
